import React, { useEffect, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { useFocusedRow } from "hooks/useFocusedRecord";
import {
  FocusNotFound,
  focusRowClass,
  scrollRowIntoView,
} from "Common/FocusedRow";
import { AlertTriangle, Clock, CheckCircle, XCircle, Search, RefreshCw } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getCsrSummary, getAllCsrTickets, updateCsrStatus, CsrTicket } from "services/csr.service";
import Modal from "Common/Components/Modal";

const STATUS_COLORS: Record<string, string> = {
  open: "bg-blue-100 text-blue-800",
  in_progress: "bg-yellow-100 text-yellow-800",
  resolved: "bg-green-100 text-green-800",
  closed: "bg-gray-100 text-gray-600",
};

const PRIORITY_COLORS: Record<string, string> = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-orange-100 text-orange-700",
  urgent: "bg-red-100 text-red-700",
};

const NEXT_STATUSES: Record<string, { value: string; label: string }[]> = {
  open: [{ value: "in_progress", label: "Mark In Progress" }],
  in_progress: [
    { value: "resolved", label: "Mark Resolved" },
    { value: "closed", label: "Close Ticket" },
  ],
  resolved: [{ value: "closed", label: "Close Ticket" }],
  closed: [],
};

const SummaryCard = ({
  label,
  value,
  color,
  icon: Icon,
}: {
  label: string;
  value: number;
  color: string;
  icon: React.ElementType;
}) => (
  <div className="bg-white rounded-lg border border-gray-200 p-5 flex items-center gap-4">
    <div className={`p-3 rounded-lg ${color}`}>
      <Icon size={20} />
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  </div>
);

const CsrTickets = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("");
  const [slaFilter, setSlaFilter] = useState<"" | "true">("");
  const [selectedTicket, setSelectedTicket] = useState<CsrTicket | null>(null);
  const [newStatus, setNewStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [showModal, setShowModal] = useState(false);
  const { data: summaryData } = useQuery("csr-summary", getCsrSummary);
  const { data: ticketsData, isLoading, isError, refetch } = useQuery(
    ["csr-tickets", statusFilter, slaFilter],
    () =>
      getAllCsrTickets({
        status: statusFilter || undefined,
        slaBreached: slaFilter === "true" ? true : undefined,
      })
  );

  const { isLoading: saving, mutate: doUpdate } = useMutation(
    () => updateCsrStatus(selectedTicket!._id, newStatus, notes || undefined),
    {
      onSuccess() {
        toast.success("Ticket updated");
        queryClient.invalidateQueries("csr-tickets");
        queryClient.invalidateQueries("csr-summary");
        closeModal();
      },
      onError(err: any) {
        toast.error(err?.response?.data?.message || "Update failed");
      },
    }
  );

  const summary = summaryData?.data;
  // The endpoint is paginated now — `data` is a page object, not an array.
  const tickets: CsrTicket[] = ticketsData?.data?.data ?? [];
  const ticketTotal = ticketsData?.data?.total ?? 0;

  /**
   * Highlights the ticket an operator followed here from an SLA-breach alert.
   *
   * Highlight rather than opening the modal: the modal is a status-change form,
   * and springing one open on arrival invites a mis-click on someone else's
   * ticket. The row is scrolled to and ringed; acting on it stays deliberate.
   */
  const { notFound: focusNotFound, dismissFocus, isFocused } = useFocusedRow(
    tickets,
    isLoading
  );

  const openModal = (ticket: CsrTicket) => {
    const options = NEXT_STATUSES[ticket.status] ?? [];
    if (options.length === 0) return;
    setSelectedTicket(ticket);
    setNewStatus(options[0].value);
    setNotes("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTicket(null);
    setNotes("");
  };

  const formatSla = (ticket: CsrTicket) => {
    const due = new Date(ticket.slaDueAt);
    const now = new Date();
    if (ticket.slaBreached) return { label: "Breached", cls: "text-red-600 font-semibold" };
    const hoursLeft = Math.round((due.getTime() - now.getTime()) / 36e5);
    if (hoursLeft <= 24) return { label: `${hoursLeft}h left`, cls: "text-orange-500 font-medium" };
    const daysLeft = Math.floor(hoursLeft / 24);
    return { label: `${daysLeft}d left`, cls: "text-gray-500" };
  };

  const requesterName = (t: CsrTicket) =>
    t.userId ? `${t.userId.firstName} ${t.userId.lastName}` : t.name || "Guest";

  const requesterEmail = (t: CsrTicket) =>
    t.userId?.email || t.email || "—";

  return (
    <React.Fragment>
      <BreadCrumb title="CSR Tickets" pageTitle="Customer Service" />
      <ToastContainer closeButton={false} limit={1} />

      {focusNotFound && <FocusNotFound onDismiss={dismissFocus} />}

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            label="Open"
            value={summary.open}
            color="bg-blue-100 text-blue-600"
            icon={Clock}
          />
          <SummaryCard
            label="In Progress"
            value={summary.inProgress}
            color="bg-yellow-100 text-yellow-600"
            icon={RefreshCw}
          />
          <SummaryCard
            label="Resolved"
            value={summary.resolved}
            color="bg-green-100 text-green-600"
            icon={CheckCircle}
          />
          <SummaryCard
            label="SLA Breached"
            value={summary.breached}
            color="bg-red-100 text-red-600"
            icon={AlertTriangle}
          />
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex flex-wrap gap-3 items-center justify-between p-4">
          <div className="flex gap-3 flex-wrap">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-200 rounded px-3 py-2 text-sm"
            >
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <select
              value={slaFilter}
              onChange={(e) => setSlaFilter(e.target.value as "" | "true")}
              className="border border-gray-200 rounded px-3 py-2 text-sm"
            >
              <option value="">All SLA</option>
              <option value="true">Breached Only</option>
            </select>
          </div>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
          >
            <RefreshCw className="size-4" /> Refresh
          </button>
        </div>

        <div className="overflow-x-auto">
          {isError ? (
            <div className="py-12 text-center">
              <p className="text-slate-500 mb-3">Couldn't load tickets</p>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                <RefreshCw className="size-4" /> Retry
              </button>
            </div>
          ) : isLoading ? (
            <div className="py-12 text-center text-gray-400">Loading...</div>
          ) : tickets.length === 0 ? (
            <div className="py-12 text-center">
              <Search className="size-6 mx-auto text-sky-500 fill-sky-100 mb-2" />
              <p className="text-slate-500">No tickets found</p>
            </div>
          ) : (
            <>
            {ticketTotal > tickets.length ? (
              <p className="px-1 pb-2 text-xs text-slate-500">
                Showing {tickets.length} of {ticketTotal} tickets — narrow the
                filters to see the rest.
              </p>
            ) : null}
            <table data-stack className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Ticket #</th>
                  <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Subject</th>
                  <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Requester</th>
                  <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Category</th>
                  <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Priority</th>
                  <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Status</th>
                  <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">SLA</th>
                  <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Created</th>
                  <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => {
                  const sla = formatSla(ticket);
                  const actions = NEXT_STATUSES[ticket.status] ?? [];
                  return (
                    <tr
                      key={ticket._id}
                      ref={scrollRowIntoView(isFocused(ticket._id))}
                      className={`${ticket.slaBreached ? "bg-red-50" : ""} ${focusRowClass(isFocused(ticket._id))}`}
                    >
                      <td data-label="Ticket #" data-cell="title" className="px-3.5 py-2.5 border border-gray-200 font-mono text-xs text-indigo-700">
                        {ticket.ticketNumber}
                      </td>
                      <td data-label="Subject" data-cell="block" className="px-3.5 py-2.5 border border-gray-200 max-w-[200px] truncate">
                        {ticket.subject}
                      </td>
                      <td data-label="Requester" data-cell="block" className="px-3.5 py-2.5 border border-gray-200">
                        <div className="font-medium">{requesterName(ticket)}</div>
                        <div className="text-xs text-gray-400">{requesterEmail(ticket)}</div>
                      </td>
                      <td data-label="Category" className="px-3.5 py-2.5 border border-gray-200 capitalize">{ticket.category}</td>
                      <td data-label="Priority" className="px-3.5 py-2.5 border border-gray-200">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[ticket.priority] ?? "bg-gray-100 text-gray-600"}`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td data-label="Status" className="px-3.5 py-2.5 border border-gray-200">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[ticket.status] ?? "bg-gray-100 text-gray-600"}`}>
                          {ticket.status.replace("_", " ")}
                        </span>
                      </td>
                      <td data-label="SLA" className={`px-3.5 py-2.5 border border-gray-200 text-xs ${sla.cls}`}>
                        {sla.label}
                      </td>
                      <td data-label="Created" className="px-3.5 py-2.5 border border-gray-200 text-xs text-gray-500">
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </td>
                      <td data-label="Action" data-cell="actions" className="px-3.5 py-2.5 border border-gray-200">
                        {actions.length > 0 && (
                          <button
                            onClick={() => openModal(ticket)}
                            className="text-xs px-2 py-1 rounded bg-custom-500 text-white hover:bg-custom-600"
                          >
                            Update
                          </button>
                        )}
                        {ticket.status === "closed" && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <XCircle className="size-3" /> Closed
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </>
          )}
        </div>
      </div>

      <Modal
        show={showModal}
        onHide={closeModal}
        id="csrUpdateModal"
        modal-center="true"
        size="md"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b border-slate-200"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500"
        >
          <Modal.Title className="text-16">Update Ticket Status</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 flex flex-col gap-4">
          {selectedTicket && (
            <div className="text-sm text-gray-600 space-y-1 bg-gray-50 rounded p-3">
              <p>
                <span className="font-medium">Ticket:</span>{" "}
                <span className="font-mono text-indigo-700">{selectedTicket.ticketNumber}</span>
              </p>
              <p><span className="font-medium">Subject:</span> {selectedTicket.subject}</p>
              <p><span className="font-medium">Requester:</span> {requesterName(selectedTicket)}</p>
            </div>
          )}

          {selectedTicket && NEXT_STATUSES[selectedTicket.status]?.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Status</label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                {NEXT_STATUSES[selectedTicket.status].map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              placeholder="Add resolution notes or comments..."
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => doUpdate()}
              disabled={saving}
              className="flex-1 py-2 bg-indigo-600 text-white rounded text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : selectedTicket ? (NEXT_STATUSES[selectedTicket.status]?.[0]?.label ?? "Save") : "Save"}
            </button>
            <button
              onClick={closeModal}
              className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default CsrTickets;
